import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useVideoTutorial } from '@/context/VideoTutorialContext';
import { config } from '@/config';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Lock, LogOut, Star, ExternalLink, FolderPlus, Edit2, Trash2, GripVertical, Folder, ChevronDown, ChevronUp, ChevronRight, X, Settings, LayoutGrid, List } from 'lucide-react';
import Layout from '@/components/Layout';
import { SecteurSelectorModal, userCanSwitchSecteur } from '@/components/SecteurSelector';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import InactiveApplicationDialog from '@/components/InactiveApplicationDialog';
import PendingDocumentsAppIndicator from '@/components/PendingDocumentsAppIndicator';
import { useGroup } from '@/context/GroupContext';
import { isPrysmeAccessApp } from '@/utils/prysmeAccessApp';
import {
  isAppDisabled,
  isAppOpenBlocked,
  isAppOutOfSecteurScope,
  getDisabledAppBodyClickTitle,
  getAppOpenButtonClassName,
  getDisabledAppCardClass,
  getDisabledAppLinkClass,
  getDisabledAppChipClass,
} from '@/utils/applicationHomeStatus';
import { AppSecteurScopeContext, useAppSecteurScope } from '@/context/AppSecteurScopeContext';

const BACKEND_URL = config.BACKEND_URL;
const API = BACKEND_URL;

// Compact App Icon for favorites
const CompactAppIcon = ({ app, onOpenApp, onToggleFavorite }) => {
  const [showRemove, setShowRemove] = useState(false);
  const scopeCtx = useAppSecteurScope();
  const disabled = isAppDisabled(app, scopeCtx);
  
  return (
    <div
      className={`flex flex-col items-center gap-1 group relative pt-2 px-2 ${disabled ? 'opacity-55 saturate-50' : ''}`}
      data-testid={`favorite-app-${app.id}`}
      onMouseEnter={() => setShowRemove(true)}
      onMouseLeave={() => setShowRemove(false)}
    >
      <div className="relative">
        <div
          onClick={() => onOpenApp(app)}
          className={`text-xs font-medium cursor-pointer ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-[#080808] dark:text-white hover:underline'}`}
        >
          {app.name}
        </div>
        {/* Remove from favorites button - with enough space */}
        {showRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(app.id);
            }}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg z-20 transition-all"
            data-testid={`remove-favorite-${app.id}`}
          >
            <span className="text-white text-sm font-bold leading-none">×</span>
          </button>
        )}
      </div>
      <span className="text-xs text-gray-700 dark:text-gray-300 text-center max-w-[70px] truncate">{app.name}</span>
    </div>
  );
};

// Simple App Card (sans drag) - pour les apps dans les groupes étendus
const SimpleAppCard = ({ app, user, isFavorite, onToggleFavorite, onOpenApp, onShowDetails, hasPendingDocuments }) => {
  const scopeCtx = useAppSecteurScope();
  const disabled = isAppDisabled(app, scopeCtx);
  const openBlocked = isAppOpenBlocked(app, user, scopeCtx);
  const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
  return (
    <div
      className={getDisabledAppCardClass(
        disabled,
        `rounded-xl shadow-md dark:shadow-gray-900 relative group w-full min-w-[180px] max-w-[220px] h-48 flex flex-col hover:shadow-lg hover:scale-[1.02] transition-shadow bg-white dark:bg-gray-800`
      )}
      data-testid={`app-card-${app.id}`}
    >
      <PendingDocumentsAppIndicator active={showPendingPulse} roundedClass="rounded-xl" variant="border" />
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 rounded-t-xl">
        <button
          data-testid={`favorite-button-${app.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(app.id);
          }}
          className="flex-shrink-0 z-10"
          title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        >
          {isFavorite ? (
            <Star size={18} className="text-[#f4af14] fill-[#f4af14]" />
          ) : (
            <Star size={18} className="text-gray-300 hover:text-[#f4af14]" />
          )}
        </button>
        <h3 className={`text-sm font-bold truncate flex-1 ${
          disabled
            ? 'text-gray-400 dark:text-gray-500'
            : showPendingPulse
              ? 'pending-docs-app-list-name-blink font-semibold'
              : 'text-[#080808] dark:text-white'
        }`}>{app.name}</h3>
      </div>

      {/* Center - Description */}
      <div 
        className={`flex-1 px-4 py-2 flex items-start cursor-pointer transition-colors ${
          disabled ? 'hover:bg-gray-200/40 dark:hover:bg-gray-700/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}
        onClick={() => (disabled ? onOpenApp(app) : onShowDetails && onShowDetails(app))}
        title={getDisabledAppBodyClickTitle(app, user, scopeCtx)}
      >
        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-4">
          {app.description || "Aucune description disponible"}
        </p>
      </div>

      {/* Footer - Open button */}
      <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700">
        <Button
          data-testid={`open-app-button-${app.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenApp(app);
          }}
          className={getAppOpenButtonClassName(disabled)}
        >
          {openBlocked ? 'Indisponible' : <>Ouvrir <ExternalLink size={12} className="ml-1" /></>}
        </Button>
      </div>
    </div>
  );
};

// Sortable App Card - pour "Mes Applications" uniquement
const SortableAppCard = ({ app, user, isFavorite, onToggleFavorite, onOpenApp, onShowDetails, hasPendingDocuments }) => {
  const scopeCtx = useAppSecteurScope();
  const disabled = isAppDisabled(app, scopeCtx);
  const openBlocked = isAppOpenBlocked(app, user, scopeCtx);
  const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({ id: app.id });

  // Pendant le drag, la carte originale devient un placeholder transparent
  // Le DragOverlay affiche la vraie carte qui suit la souris
  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={getDisabledAppCardClass(
        disabled,
        `rounded-xl shadow-md dark:shadow-gray-900 relative group w-full min-w-[180px] max-w-[220px] h-48 flex flex-col bg-white dark:bg-gray-800 ${
          isDragging
            ? 'ring-2 ring-dashed ring-gray-300 dark:ring-gray-600 bg-gray-100 dark:bg-gray-700'
            : disabled
              ? ''
              : 'hover:shadow-lg hover:scale-[1.02] transition-shadow'
        }`
      )}
      data-testid={`app-card-${app.id}`}
    >
      <PendingDocumentsAppIndicator
        active={showPendingPulse && !isDragging}
        roundedClass="rounded-xl"
        variant="border"
      />
      {/* Header - entire zone is draggable */}
      <div 
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${app.id}`}
        className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 cursor-grab active:cursor-grabbing hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-xl"
        title="Glissez pour réorganiser ou déposer dans un groupe"
      >
        <button
          data-testid={`favorite-button-${app.id}`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggleFavorite(app.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex-shrink-0 z-10"
          title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        >
          {isFavorite ? (
            <Star size={18} className="text-[#f4af14] fill-[#f4af14]" />
          ) : (
            <Star size={18} className="text-gray-300 hover:text-[#f4af14]" />
          )}
        </button>
        <h3 className={`text-sm font-bold truncate flex-1 ${
          disabled
            ? 'text-gray-400 dark:text-gray-500'
            : showPendingPulse
              ? 'pending-docs-app-list-name-blink font-semibold'
              : 'text-[#080808] dark:text-white'
        }`}>{app.name}</h3>
        <GripVertical size={16} className="text-gray-300 flex-shrink-0" />
      </div>

      {/* Center - Description */}
      <div 
        data-testid={`app-description-${app.id}`}
        className={`flex-1 px-4 py-2 flex items-start cursor-pointer transition-colors ${
          disabled ? 'hover:bg-gray-200/40 dark:hover:bg-gray-700/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}
        onClick={() => (disabled ? onOpenApp(app) : onShowDetails && onShowDetails(app))}
        title={getDisabledAppBodyClickTitle(app, user, scopeCtx)}
      >
        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-4">
          {app.description || "Aucune description disponible"}
        </p>
      </div>

      {/* Footer - Open button */}
      <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700">
        <Button
          data-testid={`open-app-button-${app.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenApp(app);
          }}
          className={getAppOpenButtonClassName(disabled)}
        >
          {openBlocked ? 'Indisponible' : <>Ouvrir <ExternalLink size={12} className="ml-1" /></>}
        </Button>
      </div>
    </div>
  );
};

// Sortable app row for List view (Mes Applications)
const SortableAppListRow = ({
  app,
  user,
  isFavorite,
  onToggleFavorite,
  onOpenApp,
  onOpenAppDetails,
  preferences,
  hasPendingDocuments,
}) => {
  const scopeCtx = useAppSecteurScope();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: app.id,
    data: { type: 'app', appId: app.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const disabled = isAppDisabled(app, scopeCtx);
  const openBlocked = isAppOpenBlocked(app, user, scopeCtx);
  const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${disabled ? 'bg-gray-100/80 dark:bg-gray-800/60 opacity-60 saturate-[0.45]' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'} ${isDragging ? 'bg-gray-100 dark:bg-gray-700 z-10' : ''}`}
    >
      <td className="px-4 py-3 w-16">
        <div className="flex items-center gap-1">
          <div
            {...attributes}
            {...listeners}
            className="p-1 cursor-grab active:cursor-grabbing hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors touch-none"
            title="Glissez pour réorganiser"
            data-testid={`app-drag-list-${app.id}`}
          >
            <GripVertical size={16} className="text-gray-400" />
          </div>
          <button
            onClick={() => onToggleFavorite(app.id)}
            title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
          >
            {isFavorite ? (
              <Star size={18} className="text-[#f4af14] fill-[#f4af14]" />
            ) : (
              <Star size={18} className="text-gray-300 hover:text-[#f4af14]" />
            )}
          </button>
        </div>
      </td>
      <td className="px-4 py-3">
        <span
          className={`font-medium ${
            disabled
              ? 'text-gray-400 dark:text-gray-500'
              : showPendingPulse
                ? 'pending-docs-app-list-name-blink font-semibold'
                : 'text-gray-800 dark:text-white'
          }`}
        >
          {app.name}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{app.description || "—"}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenAppDetails(app)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
            title="Détails"
          >
            <Settings size={14} className="text-gray-500" />
          </button>
          <Button
            onClick={() => onOpenApp(app)}
            className={getAppOpenButtonClassName(disabled, { fullWidth: false, compact: true })}
          >
            {openBlocked ? 'Indisponible' : 'Ouvrir'}
          </Button>
        </div>
      </td>
    </tr>
  );
};

/** Priorité au pointeur dans une zone (lignes de tableau étroites) puis closestCenter en secours */
const homeDndCollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) return pointerHits;
  return closestCenter(args);
};

// Sortable & Droppable Group Folder Wrapper
const SortableGroupFolder = ({ group, children, isDraggingGroup }) => {
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `group-${group.id}`,
    data: {
      type: 'group',
      groupId: group.id,
    },
  });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `group-drop-${group.id}`,
    data: {
      groupId: group.id,
      type: 'group-drop',
    },
  });

  // Combiner les refs
  const setNodeRef = (node) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`${isDragging ? 'z-50' : ''}`}
    >
      {React.cloneElement(children, { 
        isOver: isOver && !isDraggingGroup, // Ne pas montrer isOver si on drag un groupe
        isDragging,
        dragHandleProps: { ...attributes, ...listeners },
      })}
    </div>
  );
};

// Sortable group row for List view
const SortableGroupListRow = ({
  group,
  apps,
  user,
  expandedListGroups,
  setExpandedListGroups,
  onManageGroup,
  onEditGroup,
  onDeleteGroup,
  onOpenApp,
  onToggleFavorite,
  preferences,
  hasPendingDocuments,
}) => {
  const scopeCtx = useAppSecteurScope();
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `group-${group.id}`,
    data: { type: 'group', groupId: group.id },
  });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `group-drop-${group.id}`,
    data: { type: 'group-drop', groupId: group.id },
  });

  const setRowRef = (node) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dropHighlight =
    isOver && !isDragging
      ? 'ring-2 ring-inset ring-[#f76f4b] bg-orange-50/40 dark:bg-orange-950/20'
      : '';

  return (
    <React.Fragment>
      <tr
        ref={setRowRef}
        style={style}
        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 min-h-[52px] ${isDragging ? 'bg-gray-100 dark:bg-gray-700 z-10' : ''} ${dropHighlight}`}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-1">
            <div
              {...attributes}
              {...listeners}
              className="p-1 cursor-grab active:cursor-grabbing hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors touch-none"
              title="Glissez pour réorganiser les groupes"
              data-testid={`group-drag-list-${group.id}`}
            >
              <GripVertical size={16} className="text-gray-400" />
            </div>
            <button
              onClick={() => setExpandedListGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              title={expandedListGroups[group.id] ? "Réduire" : "Développer"}
              data-testid={`toggle-list-group-${group.id}`}
            >
              {expandedListGroups[group.id] ? (
                <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronRight size={16} className="text-gray-500 dark:text-gray-400" />
              )}
            </button>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Folder size={18} className="text-[#f76f4b]" />
            <span className="font-medium text-gray-800 dark:text-white">{group.name}</span>
            <span className="text-xs text-gray-400">({apps.length})</span>
          </div>
        </td>
        <td className="px-4 py-3">
          {!expandedListGroups[group.id] && (
            <div className="flex flex-wrap gap-1">
              {apps.slice(0, 3).map(app => {
                const disabled = isAppDisabled(app, scopeCtx);
                const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
                return (
                <span
                  key={app.id}
                  className={`${getDisabledAppChipClass(disabled)} ${!disabled && showPendingPulse ? 'pending-docs-app-list-name-blink font-semibold' : ''}`}
                  onClick={() => onOpenApp(app)}
                >
                  {app.name}
                </span>
                );
              })}
              {apps.length > 3 && (
                <span className="text-xs text-gray-500">+{apps.length - 3} autres</span>
              )}
              {apps.length === 0 && (
                <span className="text-xs text-gray-400 italic">Aucune application</span>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={() => onManageGroup(group)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
              title="Gérer"
            >
              <Settings size={14} className="text-gray-500" />
            </button>
            <button
              onClick={() => onEditGroup(group)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
              title="Renommer"
            >
              <Edit2 size={14} className="text-gray-500" />
            </button>
            <button
              onClick={() => onDeleteGroup(group.id)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
              title="Supprimer"
            >
              <Trash2 size={14} className="text-red-500" />
            </button>
          </div>
        </td>
      </tr>
      {expandedListGroups[group.id] && apps.length > 0 && (
        <tr>
          <td colSpan={4} className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50">
            <div className="ml-8 flex flex-wrap gap-2 min-h-[40px]">
              {apps.map(app => {
                const disabled = isAppDisabled(app, scopeCtx);
                const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
                return (
                <div
                  key={app.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-sm transition-shadow cursor-pointer ${
                    disabled
                      ? 'bg-gray-200/80 dark:bg-gray-700/80 opacity-60 saturate-50'
                      : 'bg-white dark:bg-gray-800 hover:shadow-md'
                  }`}
                  onClick={() => onOpenApp(app)}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(app.id);
                    }}
                    className="flex-shrink-0"
                    title={preferences.favorites.includes(app.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                  >
                    {preferences.favorites.includes(app.id) ? (
                      <Star size={14} className="text-[#f4af14] fill-[#f4af14]" />
                    ) : (
                      <Star size={14} className="text-gray-300 hover:text-[#f4af14]" />
                    )}
                  </button>
                  <span className={`text-sm font-medium ${
                    disabled
                      ? 'text-gray-400 dark:text-gray-500'
                      : showPendingPulse
                        ? 'pending-docs-app-list-name-blink font-semibold'
                        : 'text-gray-800 dark:text-white'
                  }`}>{app.name}</span>
                  <ExternalLink size={12} className={disabled ? 'text-gray-300' : 'text-gray-400'} />
                </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
      {expandedListGroups[group.id] && apps.length === 0 && (
        <tr>
          <td colSpan={4} className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50">
            <div className="ml-8 text-sm text-gray-400 italic min-h-[40px]">Aucune application dans ce groupe</div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// Group Folder Component
const GroupFolder = ({ group, apps, isFavorite, onToggleFavorite, onOpenApp, onEditGroup, onDeleteGroup, onManageGroup, onRemoveApp, onShowDetails, isExpanded, onToggleExpand, isOver, isDragging, dragHandleProps, hasPendingDocuments }) => {
  const scopeCtx = useAppSecteurScope();
  const MAX_VISIBLE_APPS = 4;
  const visibleApps = apps.slice(0, MAX_VISIBLE_APPS);
  const remainingCount = Math.max(0, apps.length - MAX_VISIBLE_APPS);

  // Truncate app name to fit in the card
  const truncateName = (name, maxLength = 18) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength - 3) + '...';
  };

  return (
    <div className="mb-4" data-testid={`group-${group.id}`}>
      {/* Carte du groupe - toujours affichée, clic ouvre le modal */}
      <div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900 relative group w-full min-w-[180px] max-w-[220px] h-48 flex flex-col ${
            isDragging 
              ? 'ring-2 ring-dashed ring-gray-300 dark:ring-gray-600 bg-gray-100 dark:bg-gray-700' 
              : isOver 
                ? 'ring-4 ring-[#f76f4b] ring-opacity-50 scale-105 shadow-xl' 
                : 'hover:shadow-lg hover:scale-[1.02] transition-shadow'
          }`}
          data-testid={`group-folder-${group.id}`}
        >
          {/* Header - zone de drag */}
          <div 
            {...dragHandleProps}
            data-testid={`group-drag-${group.id}`}
            className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 cursor-grab active:cursor-grabbing hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-xl"
            title="Glissez pour réorganiser les groupes"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Folder size={18} className="text-[#f76f4b] flex-shrink-0" />
              <h3 className="text-sm font-bold text-[#080808] dark:text-white truncate">{group.name}</h3>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  data-testid={`group-edit-${group.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditGroup(group);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  title="Renommer"
                >
                  <Edit2 size={14} className="text-gray-500" />
                </button>
                <button
                  data-testid={`group-delete-${group.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteGroup(group.id);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  title="Supprimer"
                >
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
              <GripVertical size={16} className="text-gray-300 ml-1" />
            </div>
          </div>

          {/* App list - clickable to open modal */}
          <div 
            data-testid={`group-expand-${group.id}`}
            className="flex-1 px-4 py-2 flex flex-col justify-start cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => onToggleExpand(group.id)}
          >
            {visibleApps.length > 0 ? (
              <div className="space-y-1.5">
                {visibleApps.map((app) => {
                  const disabled = isAppDisabled(app, scopeCtx);
                  const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
                  return (
                  <div
                    key={app.id}
                    className={`rounded-md ${getDisabledAppLinkClass(disabled)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenApp(app);
                    }}
                    title={app.name}
                  >
                    <span className={`block truncate px-0.5 ${!disabled && showPendingPulse ? 'pending-docs-app-list-name-blink font-semibold' : ''}`}>{truncateName(app.name)}</span>
                  </div>
                  );
                })}
                {remainingCount > 0 && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                    +{remainingCount} autre{remainingCount > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                Aucune application
              </div>
            )}
          </div>

          {/* Footer with expand indicator - clickable */}
          <div 
            className="px-4 pb-2 pt-1 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-b-xl"
            onClick={() => onToggleExpand(group.id)}
          >
            <span className="text-xs text-gray-400">{apps.length} app{apps.length > 1 ? 's' : ''}</span>
            <ChevronDown size={16} className="text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const { user, logout, token, updateUser } = useAuth();
  const { isHelpPanelOpen } = useVideoTutorial();
  const { pendingDocuments, hasBlockingPendingDocuments } = useGroup();
  const hasPendingDocuments = pendingDocuments.length > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [secteurScope, setSecteurScope] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showGroupManageDialog, setShowGroupManageDialog] = useState(false);
  const [managingGroup, setManagingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [openGroupModal, setOpenGroupModal] = useState(null); // ID du groupe dont le modal est ouvert
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [showAppDetailsDialog, setShowAppDetailsDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [inactiveAppDialog, setInactiveAppDialog] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const lastOverIdRef = useRef(null);
  const [showSecteurModal, setShowSecteurModal] = useState(false);
  
  // Charger les préférences d'affichage depuis localStorage
  const loadViewPreferences = () => {
    try {
      const saved = localStorage.getItem('homeViewPreferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading view preferences:', e);
    }
    return null;
  };
  
  const savedPrefs = loadViewPreferences();
  const [groupsViewMode, setGroupsViewMode] = useState(savedPrefs?.groupsViewMode || 'cards');
  const [appsViewMode, setAppsViewMode] = useState(savedPrefs?.appsViewMode || 'cards');
  const [groupsSectionExpanded, setGroupsSectionExpanded] = useState(savedPrefs?.groupsSectionExpanded ?? true);
  const [appsSectionExpanded, setAppsSectionExpanded] = useState(savedPrefs?.appsSectionExpanded ?? true);
  const [expandedListGroups, setExpandedListGroups] = useState(savedPrefs?.expandedListGroups || {});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  // Trouver l'élément en cours de drag (application ou groupe)
  const activeApp = activeId && !String(activeId).startsWith('group-') 
    ? applications.find(app => app.id === activeId) 
    : null;
  
  const activeGroup = activeId && String(activeId).startsWith('group-')
    ? preferences?.groups?.find(g => `group-${g.id}` === activeId)
    : null;
  
  // Trouver les apps du groupe actif pour l'overlay
  const activeGroupApps = activeGroup 
    ? applications.filter(app => activeGroup.appIds?.includes(app.id))
    : [];

  // Sauvegarder les préférences d'affichage dans localStorage
  useEffect(() => {
    const viewPreferences = {
      groupsViewMode,
      appsViewMode,
      groupsSectionExpanded,
      appsSectionExpanded,
      expandedListGroups,
    };
    localStorage.setItem('homeViewPreferences', JSON.stringify(viewPreferences));
  }, [groupsViewMode, appsViewMode, groupsSectionExpanded, appsSectionExpanded, expandedListGroups]);

  // Un seul chargement : au login / changement user, et au retour sur « / » (évite le double fetch au mount)
  useEffect(() => {
    if (!user || location.pathname !== '/') return;
    loadData();
  }, [user, location.pathname]);

  // Modal secteur : ≥ 2 secteurs et préférence « Ne plus afficher » absente
  useEffect(() => {
    if (!user || !token || !preferences) return;
    if (userCanSwitchSecteur(user) && preferences.hide_secteur_modal !== true) {
      setShowSecteurModal(true);
    } else {
      setShowSecteurModal(false);
    }
  }, [user, token, preferences]);

  // Périmètre apps : société du secteur actif
  useEffect(() => {
    if (!user || !token) {
      setSecteurScope(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/auth/secteurs-autorises?token=${token}`);
        const secteurs = response.data.secteurs || [];
        const actifId =
          response.data.actif_secteur ||
          user.actif_secteur ||
          user.service_id ||
          null;
        const actif = secteurs.find((s) => String(s.id) === String(actifId));
        if (!cancelled) {
          setSecteurScope({
            actifSecteurId: actifId,
            actifCompanyId: actif?.company_id || null,
          });
        }
      } catch (e) {
        console.error('Erreur chargement périmètre secteurs:', e);
        if (!cancelled) {
          setSecteurScope({
            actifSecteurId: user.actif_secteur || user.service_id || null,
            actifCompanyId: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, token, user?.actif_secteur, user?.service_id]);

  const scopeCtx = secteurScope;

  const loadData = async () => {
    try {
      const [appsRes, prefsRes, maintenanceRes] = await Promise.all([
        axios.get(`${API}/applications`),
        axios.get(`${API}/preferences/${user.id}`),
        axios.get(`${API}/maintenance-mode`).catch(() => ({ data: { is_active: false } })),
      ]);

      // Filtrer les applications autorisées pour l'utilisateur
      // ET exclure les applications de type "external" (destinées uniquement aux clients)
      const userApps = appsRes.data.filter(app => 
        user.application_ids.includes(app.id) && 
        app.auth_type !== 'external' &&
        app.is_visible !== false
      );
      setApplications(userApps);
      setMaintenanceActive(maintenanceRes.data.is_active || false);
      setPreferences(prefsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleToggleFavorite = async (appId) => {
    const newFavorites = preferences.favorites.includes(appId)
      ? preferences.favorites.filter(id => id !== appId)
      : [...preferences.favorites, appId];

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        favorites: newFavorites,
      });
      setPreferences(response.data);
      toast.success(newFavorites.includes(appId) ? 'Ajouté aux favoris' : 'Retiré des favoris');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des favoris');
    }
  };

  // Réorganiser les favoris
  const handleReorderFavorites = async (newFavoritesOrder) => {
    // Update state immediately for instant feedback
    setPreferences(prev => ({
      ...prev,
      favorites: newFavoritesOrder,
    }));

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        favorites: newFavoritesOrder,
      });
      setPreferences(response.data);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      // Rollback on error
      await loadData();
    }
  };

  const handleOpenApp = async (app) => {
    if (isAppOutOfSecteurScope(app, scopeCtx)) {
      toast.error('Cette application n\'est pas disponible sur ce secteur. Changez de secteur pour y accéder.');
      return;
    }
    if (isAppOpenBlocked(app, user, scopeCtx)) {
      setInactiveAppDialog(app);
      return;
    }
    if (hasBlockingPendingDocuments && !isPrysmeAccessApp(app)) {
      toast.error("Signez vos documents obligatoires dans Prysm'Access pour continuer.");
      window.dispatchEvent(new CustomEvent('open-prysme-access-documents'));
      return;
    }
    try {
      // Track access
      await axios.post(`${API}/preferences/${user.id}/access/${app.id}`);
      
      // Generate SSO token
      const token = sessionStorage.getItem('token');
      const ssoResponse = await axios.post(`${API}/auth/sso-token?token=${token}`);
      
      const ssoToken = ssoResponse.data.sso_token;
      
      let ssoUrl;
      
      // Si un template SSO est défini, l'utiliser
      if (app.sso_url_template) {
        // Remplacer les variables dans le template (insensible à la casse)
        let template = app.sso_url_template;
        template = template.replace(/{TOKEN}/gi, ssoToken);
        template = template.replace(/{USERNAME}/gi, user.username || '');
        template = template.replace(/{EMAIL}/gi, user.email_principal || user.email_secondaire || '');
        template = template.replace(/{USER_ID}/gi, user.id || '');
        template = template.replace(/{FIRST_NAME}/gi, user.first_name || '');
        template = template.replace(/{LAST_NAME}/gi, user.last_name || '');
        
        // Construire l'URL finale : URL de base + template (éviter le double slash)
        let baseUrl = app.url.endsWith('/') ? app.url.slice(0, -1) : app.url;
        let templatePath = template.startsWith('/') ? template : '/' + template;
        ssoUrl = baseUrl + templatePath;
      } else {
        // Comportement par défaut si pas de template
        const separator = app.url.includes('?') ? '&' : '?';
        ssoUrl = `${app.url}${separator}sso_token=${ssoToken}`;
      }
      
      window.open(ssoUrl, '_blank');
    } catch (error) {
      console.error('Error opening app:', error);
      toast.error('Erreur lors de l\'ouverture de l\'application');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Veuillez entrer un nom de groupe');
      return;
    }

    const newGroup = {
      id: `group_${Date.now()}`,
      name: newGroupName,
      appIds: [],
    };

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        groups: [...(preferences.groups || []), newGroup],
      });
      setPreferences(response.data);
      setShowGroupDialog(false);
      setNewGroupName('');
    } catch (error) {
      toast.error('Erreur lors de la création du groupe');
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setShowGroupDialog(true);
  };

  const handleUpdateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Veuillez entrer un nom de groupe');
      return;
    }

    const updatedGroups = preferences.groups.map(g =>
      g.id === editingGroup.id ? { ...g, name: newGroupName } : g
    );

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        groups: updatedGroups,
      });
      setPreferences(response.data);
      setShowGroupDialog(false);
      setNewGroupName('');
      setEditingGroup(null);
      toast.success('Groupe modifié avec succès');
    } catch (error) {
      toast.error('Erreur lors de la modification du groupe');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    const updatedGroups = preferences.groups.filter(g => g.id !== groupId);

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        groups: updatedGroups,
      });
      setPreferences(response.data);
      toast.success('Groupe supprimé avec succès');
    } catch (error) {
      toast.error('Erreur lors de la suppression du groupe');
    }
  };

  // Retirer une application d'un groupe
  const handleRemoveAppFromGroup = async (groupId, appId) => {
    const updatedGroups = preferences.groups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          appIds: (g.appIds || []).filter(id => id !== appId)
        };
      }
      return g;
    });

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        groups: updatedGroups,
      });
      setPreferences(response.data);
      toast.success('Application retirée du groupe');
    } catch (error) {
      toast.error('Erreur lors du retrait de l\'application');
    }
  };

  // Ouvrir la modale de gestion du groupe
  const handleManageGroup = (group) => {
    setManagingGroup(group);
    setShowGroupManageDialog(true);
  };

  // Ouvrir la modale de détails d'une application
  const handleOpenAppDetails = (app) => {
    setSelectedApp(app);
    setShowAppDetailsDialog(true);
  };

  // Ajouter une application à un groupe existant
  const handleAddAppToGroup = async (appId, groupId) => {
    const updatedGroups = preferences.groups.map(g => {
      if (g.id === groupId) {
        // Vérifier si l'app n'est pas déjà dans le groupe
        if (!g.appIds?.includes(appId)) {
          return {
            ...g,
            appIds: [...(g.appIds || []), appId]
          };
        }
      }
      return g;
    });

    try {
      const response = await axios.put(`${API}/preferences/${user.id}`, {
        groups: updatedGroups,
      });
      setPreferences(response.data);
      toast.success('Application ajoutée au groupe');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout au groupe');
    }
  };

  const toggleGroupExpansion = (groupId) => {
    // Ouvre le modal pour le groupe
    setOpenGroupModal(groupId);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    lastOverIdRef.current = null;
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeStr = String(active.id);
    const overStr = String(over.id);
    const isDraggingGroupRow =
      activeStr.startsWith('group-') && !activeStr.startsWith('group-drop-');
    if (isDraggingGroupRow && (overStr.startsWith('group-') || overStr.startsWith('group-drop-'))) {
      lastOverIdRef.current = over.id;
    } else if (!isDraggingGroupRow) {
      // Application : mémoriser tout survol (ligne groupe, zone drop, autre app) pour le relâchement
      lastOverIdRef.current = over.id;
    }
  };

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;

    const activeIdStr = String(active.id);

    // Vérifier si on drag un groupe
    if (activeIdStr.startsWith('group-')) {
      const overResolved = over || (lastOverIdRef.current ? { id: lastOverIdRef.current } : null);
      lastOverIdRef.current = null;

      if (!overResolved || active.id === overResolved.id) return;

      const overIdStr = String(overResolved.id);
      const activeGroupId = activeIdStr.replace('group-', '');
      const overGroupId = overIdStr.replace('group-', '').replace('group-drop-', '');
      
      // Ne permettre le drop que sur un autre groupe (ou zone group-drop)
      if (!overIdStr.startsWith('group-') && !overIdStr.startsWith('group-drop-')) return;

      const oldIndex = preferences.groups.findIndex(g => g.id === activeGroupId);
      const newIndex = preferences.groups.findIndex(g => g.id === overGroupId);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newGroups = arrayMove(preferences.groups, oldIndex, newIndex);

        // Update state immediately
        setPreferences(prev => ({
          ...prev,
          groups: newGroups,
        }));

        // Save in background
        try {
          await axios.put(`${API}/preferences/${user.id}`, {
            groups: newGroups,
          });
        } catch (error) {
          toast.error('Erreur lors de la sauvegarde');
          await loadData();
        }
      }
      return;
    }

    // C'est une application qui est draggée
    const overResolvedApp = over || (lastOverIdRef.current ? { id: lastOverIdRef.current } : null);
    lastOverIdRef.current = null;

    if (!overResolvedApp || active.id === overResolvedApp.id) return;

    const draggedAppId = active.id;
    const targetId = overResolvedApp.id;
    const overIdStr = String(overResolvedApp.id);

    // Check if dropped on a group folder
    let targetGroupId = overResolvedApp.data?.current?.groupId;
    if (!targetGroupId) {
      if (overIdStr.startsWith('group-drop-')) {
        targetGroupId = overIdStr.slice('group-drop-'.length);
      } else if (overIdStr.startsWith('group-')) {
        targetGroupId = overIdStr.slice('group-'.length);
      }
    }
    const isGroupTarget =
      overIdStr.startsWith('group-drop-') ||
      overResolvedApp.data?.current?.type === 'group-drop' ||
      (overIdStr.startsWith('group-') && !!targetGroupId);
    
    if (targetGroupId && isGroupTarget) {
      // Add app to group
      const updatedGroups = preferences.groups.map(g => {
        if (g.id === targetGroupId) {
          // Check if app is already in this group
          if (!g.appIds?.includes(draggedAppId)) {
            return {
              ...g,
              appIds: [...(g.appIds || []), draggedAppId]
            };
          }
        }
        return g;
      });

      // Update state immediately for instant feedback
      setPreferences(prev => ({
        ...prev,
        groups: updatedGroups,
      }));

      // Save in background
      try {
        await axios.put(`${API}/preferences/${user.id}`, {
          groups: updatedGroups,
        });
      } catch (error) {
        toast.error('Erreur lors de l\'ajout au groupe');
        // Rollback on error
        await loadData();
      }
      return;
    }

    // Normal reordering between apps
    const currentUngroupedApps = getGroupedApps().ungrouped;

    const oldIndex = currentUngroupedApps.findIndex(app => app.id === draggedAppId);
    const newIndex = currentUngroupedApps.findIndex(app => app.id === targetId);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(currentUngroupedApps, oldIndex, newIndex);
      const newCustomOrder = newOrder.map(app => app.id);
      
      // Update state immediately for smooth animation
      setApplications(prevApps => {
        const newApps = [...prevApps];
        const draggedApp = newApps.find(app => app.id === draggedAppId);
        const targetApp = newApps.find(app => app.id === targetId);
        
        if (draggedApp && targetApp) {
          const draggedIndex = newApps.indexOf(draggedApp);
          const targetIndex = newApps.indexOf(targetApp);
          return arrayMove(newApps, draggedIndex, targetIndex);
        }
        return newApps;
      });

      // Update preferences state
      setPreferences(prev => ({
        ...prev,
        custom_order: newCustomOrder,
      }));

      // Save in background without reloading
      try {
        await axios.put(`${API}/preferences/${user.id}`, {
          custom_order: newCustomOrder,
        });
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde de l\'ordre');
        // Rollback on error
        await loadData();
      }
    }
  };

  const getFilteredAndSortedApps = () => {
    if (!preferences) return applications;

    let filtered = applications.filter(app => {
      const query = searchQuery.toLowerCase();
      const matchesName = app.name.toLowerCase().includes(query);
      const matchesDescription = app.description?.toLowerCase().includes(query) || false;
      return matchesName || matchesDescription;
    });

    // Apply custom order if exists
    if (preferences.custom_order && preferences.custom_order.length > 0) {
      filtered.sort((a, b) => {
        const indexA = preferences.custom_order.indexOf(a.id);
        const indexB = preferences.custom_order.indexOf(b.id);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    } else {
      // Default sorting
      filtered.sort((a, b) => {
        const aFav = preferences.favorites.includes(a.id) ? 1 : 0;
        const bFav = preferences.favorites.includes(b.id) ? 1 : 0;
        if (aFav !== bFav) return bFav - aFav;

        const aLastAccess = preferences.last_accessed?.[a.id] || 0;
        const bLastAccess = preferences.last_accessed?.[b.id] || 0;
        if (aLastAccess !== bLastAccess) return bLastAccess > aLastAccess ? 1 : -1;

        const aCount = preferences.access_count?.[a.id] || 0;
        const bCount = preferences.access_count?.[b.id] || 0;
        if (aCount !== bCount) return bCount - aCount;

        return a.default_order - b.default_order;
      });
    }

    return filtered;
  };

  const getFavoriteApps = () => {
    if (!preferences || !preferences.favorites) return [];
    // Garder l'ordre défini dans preferences.favorites
    return preferences.favorites
      .map(favId => applications.find(app => app.id === favId))
      .filter(app => app !== undefined);
  };

  const getGroupedApps = () => {
    if (!preferences || !preferences.groups || preferences.groups.length === 0) {
      return { groups: [], ungrouped: getFilteredAndSortedApps() };
    }

    const result = { groups: [], ungrouped: [] };

    preferences.groups.forEach(group => {
      const groupApps = applications.filter(app => group.appIds?.includes(app.id));
      result.groups.push({
        group,
        apps: groupApps,
      });
    });

    // Ne plus filtrer les apps groupées - elles apparaissent partout
    const ungroupedApps = getFilteredAndSortedApps();
    result.ungrouped = ungroupedApps;

    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  const favoriteApps = getFavoriteApps();
  const groupedApps = getGroupedApps();

  return (
    <AppSecteurScopeContext.Provider value={secteurScope}>
    <Layout
      favoriteApps={favoriteApps}
      onOpenApp={handleOpenApp}
      onToggleFavorite={handleToggleFavorite}
      onReorderFavorites={handleReorderFavorites}
      onLogout={handleLogout}
      API={API}
      maintenanceActive={maintenanceActive}
      isAdmin={user.is_admin}
      hasNewsSidebar={!isHelpPanelOpen}
    >
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:pl-8">
        {/* Search and Filters */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-gray-300 dark:border-gray-600 focus:border-[#f76f4b] focus:ring-[#f76f4b] dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Create Group */}
            <Button
              data-testid="create-group-button"
              onClick={() => {
                setEditingGroup(null);
                setNewGroupName('');
                setShowGroupDialog(true);
              }}
              className="border-2 border-[#f76f4b] bg-transparent hover:bg-[#f76f4b]/10 text-[#f76f4b] whitespace-nowrap"
            >
              <FolderPlus size={16} className="mr-2 sm:mr-2" />
              <span className="hidden xs:inline">Créer un groupe</span>
              <span className="xs:hidden">Groupe</span>
            </Button>
          </div>
        </div>

        {/* Applications */}
        <DndContext
          sensors={sensors}
          collisionDetection={homeDndCollisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div data-testid="applications-container">
            {/* Groups as folders - Always visible */}
            <div className="mb-6" data-testid="my-groups-section">
              <div className="flex items-center gap-3 mb-4">
                {/* Expand/Collapse arrow for section */}
                <button
                  onClick={() => setGroupsSectionExpanded(!groupsSectionExpanded)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title={groupsSectionExpanded ? "Réduire la section" : "Développer la section"}
                  data-testid="toggle-groups-section"
                >
                  {groupsSectionExpanded ? (
                    <ChevronDown size={20} className="text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronRight size={20} className="text-gray-500 dark:text-gray-400" />
                  )}
                </button>
                {groupsSectionExpanded && groupedApps.groups.length > 0 && (
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1" data-testid="groups-view-toggle">
                    <button
                      data-testid="groups-view-cards"
                      onClick={() => setGroupsViewMode('cards')}
                      className={`p-1.5 rounded ${groupsViewMode === 'cards' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                      title="Vue cartes"
                    >
                      <LayoutGrid size={16} className={groupsViewMode === 'cards' ? 'text-[#f76f4b]' : 'text-gray-500'} />
                    </button>
                    <button
                      data-testid="groups-view-list"
                      onClick={() => setGroupsViewMode('list')}
                      className={`p-1.5 rounded ${groupsViewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                      title="Vue liste"
                    >
                      <List size={16} className={groupsViewMode === 'list' ? 'text-[#f76f4b]' : 'text-gray-500'} />
                    </button>
                  </div>
                )}
                <h2 className="text-xl font-bold text-[#080808] dark:text-white">Mes Groupes</h2>
              </div>
              
              {groupsSectionExpanded && (
                groupedApps.groups.length > 0 ? (
                  groupsViewMode === 'cards' ? (
                    <SortableContext
                      items={preferences.groups.map(g => `group-${g.id}`)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {groupedApps.groups.map(({ group, apps }) => (
                          <SortableGroupFolder key={group.id} group={group} isDraggingGroup={activeId?.startsWith('group-')}>
                            <GroupFolder
                              group={group}
                              apps={apps}
                              isFavorite={(appId) => preferences.favorites.includes(appId)}
                              onToggleFavorite={handleToggleFavorite}
                              onOpenApp={handleOpenApp}
                              onEditGroup={handleEditGroup}
                              onDeleteGroup={handleDeleteGroup}
                              onManageGroup={handleManageGroup}
                              onRemoveApp={handleRemoveAppFromGroup}
                              onShowDetails={handleOpenAppDetails}
                              isExpanded={expandedGroups[group.id]}
                              onToggleExpand={toggleGroupExpansion}
                              hasPendingDocuments={hasPendingDocuments}
                            />
                          </SortableGroupFolder>
                        ))}
                      </div>
                    </SortableContext>
                  ) : (
                    /* Vue liste pour les groupes avec expansion individuelle */
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                      <SortableContext
                        items={preferences.groups.map(g => `group-${g.id}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase w-16"></th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Groupe</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Applications</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {groupedApps.groups.map(({ group, apps }) => (
                              <SortableGroupListRow
                                key={group.id}
                                group={group}
                                apps={apps}
                                user={user}
                                expandedListGroups={expandedListGroups}
                                setExpandedListGroups={setExpandedListGroups}
                                onManageGroup={handleManageGroup}
                                onEditGroup={handleEditGroup}
                                onDeleteGroup={handleDeleteGroup}
                                onOpenApp={handleOpenApp}
                                onToggleFavorite={handleToggleFavorite}
                                preferences={preferences}
                                hasPendingDocuments={hasPendingDocuments}
                              />
                            ))}
                          </tbody>
                        </table>
                      </SortableContext>
                    </div>
                  )
                ) : (
                  /* Aucun groupe créé - message d'information */
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 text-center">
                    <Folder size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">Aucun groupe n'a été créé</p>
                    <Button
                      data-testid="create-first-group-button"
                      onClick={() => {
                        setEditingGroup(null);
                        setNewGroupName('');
                        setShowGroupDialog(true);
                      }}
                      className="border-2 border-[#f76f4b] bg-transparent hover:bg-[#f76f4b]/10 text-[#f76f4b]"
                    >
                      <FolderPlus size={16} className="mr-2" />
                      Créer un groupe
                    </Button>
                  </div>
                )
              )}
            </div>

            {/* Ungrouped apps */}
            {groupedApps.ungrouped.length > 0 && (
              <div data-testid="my-applications-section">
                <div className="flex items-center gap-3 mb-4">
                  {/* Expand/Collapse arrow for section */}
                  <button
                    onClick={() => setAppsSectionExpanded(!appsSectionExpanded)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    title={appsSectionExpanded ? "Réduire la section" : "Développer la section"}
                    data-testid="toggle-apps-section"
                  >
                    {appsSectionExpanded ? (
                      <ChevronDown size={20} className="text-gray-500 dark:text-gray-400" />
                    ) : (
                      <ChevronRight size={20} className="text-gray-500 dark:text-gray-400" />
                    )}
                  </button>
                  {appsSectionExpanded && (
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1" data-testid="apps-view-toggle">
                      <button
                        data-testid="apps-view-cards"
                        onClick={() => setAppsViewMode('cards')}
                        className={`p-1.5 rounded ${appsViewMode === 'cards' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        title="Vue cartes"
                      >
                        <LayoutGrid size={16} className={appsViewMode === 'cards' ? 'text-[#f76f4b]' : 'text-gray-500'} />
                      </button>
                      <button
                        data-testid="apps-view-list"
                        onClick={() => setAppsViewMode('list')}
                        className={`p-1.5 rounded ${appsViewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        title="Vue liste"
                      >
                        <List size={16} className={appsViewMode === 'list' ? 'text-[#f76f4b]' : 'text-gray-500'} />
                      </button>
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-[#080808] dark:text-white">Mes Applications</h2>
                </div>
                
                {appsSectionExpanded && (
                  appsViewMode === 'cards' ? (
                    <SortableContext
                      items={groupedApps.ungrouped.map(app => app.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {groupedApps.ungrouped.map((app) => (
                          <SortableAppCard
                            key={`ungrouped-${app.id}`}
                            app={app}
                            user={user}
                            isFavorite={preferences.favorites.includes(app.id)}
                            onToggleFavorite={handleToggleFavorite}
                            onOpenApp={handleOpenApp}
                            onShowDetails={handleOpenAppDetails}
                            hasPendingDocuments={hasPendingDocuments}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  ) : (
                    /* Vue liste pour les applications */
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                      <SortableContext
                        items={groupedApps.ungrouped.map(app => app.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase w-16"></th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Application</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Description</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {groupedApps.ungrouped.map((app) => (
                              <SortableAppListRow
                                key={app.id}
                                app={app}
                                user={user}
                                isFavorite={preferences.favorites.includes(app.id)}
                                onToggleFavorite={handleToggleFavorite}
                                onOpenApp={handleOpenApp}
                                onOpenAppDetails={handleOpenAppDetails}
                                preferences={preferences}
                                hasPendingDocuments={hasPendingDocuments}
                              />
                            ))}
                          </tbody>
                        </table>
                      </SortableContext>
                    </div>
                  )
                )}
              </div>
            )}

            {groupedApps.groups.length === 0 && groupedApps.ungrouped.length === 0 && favoriteApps.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">Aucune application disponible</p>
              </div>
            )}
          </div>

          {/* DragOverlay - suit parfaitement la souris */}
          <DragOverlay>
            {activeApp ? (
              // Overlay pour les applications (style ligne en vue liste, carte en vue cartes)
              appsViewMode === 'list' ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl ring-2 ring-[#f76f4b] px-4 py-3 flex items-center gap-4 opacity-95 cursor-grabbing min-w-[320px]">
                  <GripVertical size={18} className="text-gray-400 flex-shrink-0" />
                  {preferences?.favorites?.includes(activeApp.id) ? (
                    <Star size={18} className="text-[#f4af14] fill-[#f4af14] flex-shrink-0" />
                  ) : (
                    <Star size={18} className="text-gray-300 flex-shrink-0" />
                  )}
                  <span className="font-medium text-gray-800 dark:text-white truncate flex-1">{activeApp.name}</span>
                  <span className="text-xs text-gray-400 truncate max-w-[200px] flex-shrink-0">{activeApp.description || "—"}</span>
                </div>
              ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-2 ring-[#f76f4b] w-52 h-48 flex flex-col opacity-90 cursor-grabbing">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-t-xl">
                  <Star size={18} className={preferences?.favorites?.includes(activeApp.id) ? "text-[#f4af14] fill-[#f4af14]" : "text-gray-300"} />
                  <h3 className="text-sm font-bold text-[#080808] dark:text-white truncate flex-1">{activeApp.name}</h3>
                  <GripVertical size={16} className="text-gray-400" />
                </div>
                {/* Center */}
                <div className="flex-1 px-4 py-2 flex items-start">
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-4">
                    {activeApp.description || "Aucune description disponible"}
                  </p>
                </div>
                {/* Footer */}
                <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                  <div className="w-full border-2 border-[#f76f4b] bg-transparent text-[#f76f4b] font-medium rounded-lg py-1.5 text-xs text-center">
                    Ouvrir
                  </div>
                </div>
              </div>
              )
            ) : activeGroup ? (
              // Overlay pour les groupes (style ligne en vue liste, carte en vue cartes)
              groupsViewMode === 'list' ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl ring-2 ring-[#f76f4b] px-4 py-3 flex items-center gap-4 opacity-95 cursor-grabbing min-w-[320px]">
                  <GripVertical size={18} className="text-gray-400 flex-shrink-0" />
                  <Folder size={18} className="text-[#f76f4b] flex-shrink-0" />
                  <span className="font-medium text-gray-800 dark:text-white truncate flex-1">{activeGroup.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">({activeGroupApps.length} app{activeGroupApps.length !== 1 ? 's' : ''})</span>
                </div>
              ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-2 ring-[#f76f4b] w-52 h-48 flex flex-col opacity-90 cursor-grabbing">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-t-xl">
                  <Folder size={18} className="text-[#f76f4b]" />
                  <h3 className="text-sm font-bold text-[#080808] dark:text-white truncate flex-1">{activeGroup.name}</h3>
                  <GripVertical size={16} className="text-gray-400" />
                </div>
                {/* Center - App list */}
                <div className="flex-1 px-4 py-2 flex flex-col justify-start">
                  {activeGroupApps.length > 0 ? (
                    <div className="space-y-1.5">
                      {activeGroupApps.slice(0, 4).map((app) => (
                        <div key={app.id} className="text-xs text-gray-700 dark:text-gray-300 truncate">
                          {app.name.length > 18 ? app.name.substring(0, 15) + '...' : app.name}
                        </div>
                      ))}
                      {activeGroupApps.length > 4 && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                          +{activeGroupApps.length - 4} autre{activeGroupApps.length - 4 > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                      Aucune application
                    </div>
                  )}
                </div>
                {/* Footer */}
                <div className="px-4 pb-2 pt-1 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-gray-400">{activeGroupApps.length} app{activeGroupApps.length > 1 ? 's' : ''}</span>
                  <ChevronDown size={16} className="text-gray-400" />
                </div>
              </div>
              )
            ) : null}
          </DragOverlay>
        </DndContext>

      {/* Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent data-testid="group-dialog" className="dark:bg-gray-800 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">{editingGroup ? 'Modifier le groupe' : 'Créer un groupe'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="groupName" className="dark:text-gray-200">Nom du groupe</Label>
              <Input
                id="groupName"
                data-testid="group-name-input"
                type="text"
                placeholder="Ex: RH, Gestion, Quotidien"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="border-gray-300 dark:border-gray-600 focus:border-[#f76f4b] focus:ring-[#f76f4b] dark:bg-gray-700 dark:text-white"
              />
            </div>
            <Button
              data-testid="save-group-button"
              onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
              className="w-full bg-[#f76f4b] hover:bg-[#e55d3b] text-white"
            >
              {editingGroup ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Management Dialog */}
      <Dialog open={showGroupManageDialog} onOpenChange={setShowGroupManageDialog}>
        <DialogContent data-testid="group-manage-dialog" className="dark:bg-gray-800 dark:border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <Settings size={20} className="text-[#f76f4b]" />
              Gérer le groupe
            </DialogTitle>
          </DialogHeader>
          {managingGroup && (
            <div className="space-y-6 mt-4">
              {/* Group Name Section */}
              <div className="space-y-2">
                <Label htmlFor="manageGroupName" className="dark:text-gray-200 font-medium">Nom du groupe</Label>
                <div className="flex gap-2">
                  <Input
                    id="manageGroupName"
                    data-testid="manage-group-name-input"
                    type="text"
                    value={managingGroup.name}
                    onChange={(e) => setManagingGroup({ ...managingGroup, name: e.target.value })}
                    className="border-gray-300 dark:border-gray-600 focus:border-[#f76f4b] focus:ring-[#f76f4b] dark:bg-gray-700 dark:text-white"
                  />
                  <Button
                    data-testid="rename-group-button"
                    onClick={async () => {
                      if (!managingGroup.name.trim()) {
                        toast.error('Le nom du groupe ne peut pas être vide');
                        return;
                      }
                      const updatedGroups = preferences.groups.map(g =>
                        g.id === managingGroup.id ? { ...g, name: managingGroup.name } : g
                      );
                      try {
                        const response = await axios.put(`${API}/preferences/${user.id}`, {
                          groups: updatedGroups,
                        });
                        setPreferences(response.data);
                        toast.success('Groupe renommé avec succès');
                      } catch (error) {
                        toast.error('Erreur lors du renommage');
                      }
                    }}
                    className="bg-[#f76f4b] hover:bg-[#e55d3b] text-white"
                  >
                    Renommer
                  </Button>
                </div>
              </div>

              {/* Apps in Group Section */}
              <div className="space-y-2">
                <Label className="dark:text-gray-200 font-medium">
                  Applications dans ce groupe ({managingGroup.appIds?.length || 0})
                </Label>
                <div className="max-h-48 overflow-y-auto space-y-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  {managingGroup.appIds?.length > 0 ? (
                    applications
                      .filter(app => managingGroup.appIds?.includes(app.id))
                      .map(app => (
                        <div 
                          key={app.id} 
                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded-lg shadow-sm"
                        >
                          <span className="text-sm font-medium text-gray-800 dark:text-white">{app.name}</span>
                          <button
                            onClick={() => {
                              handleRemoveAppFromGroup(managingGroup.id, app.id);
                              setManagingGroup({
                                ...managingGroup,
                                appIds: managingGroup.appIds.filter(id => id !== app.id)
                              });
                            }}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-500"
                            title="Retirer du groupe"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      Aucune application dans ce groupe.
                      <br />
                      <span className="text-xs">Glissez-déposez des applications pour les ajouter.</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Delete Group Section */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                <Button
                  data-testid="delete-group-from-modal-button"
                  variant="destructive"
                  onClick={() => {
                    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le groupe "${managingGroup.name}" ? Les applications ne seront pas supprimées.`)) {
                      handleDeleteGroup(managingGroup.id);
                      setShowGroupManageDialog(false);
                      setManagingGroup(null);
                    }
                  }}
                  className="w-full"
                >
                  <Trash2 size={16} className="mr-2" />
                  Supprimer ce groupe
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* App Details Dialog */}
      <Dialog open={showAppDetailsDialog} onOpenChange={setShowAppDetailsDialog}>
        <DialogContent data-testid="app-details-dialog" className="dark:bg-gray-800 dark:border-gray-700 max-w-md">
          {selectedApp && (
            <>
              <DialogHeader>
                <DialogTitle className="dark:text-white flex items-center gap-3">
                  {/* Favorite button */}
                  <button
                    onClick={() => handleToggleFavorite(selectedApp.id)}
                    className="flex-shrink-0"
                    title={preferences?.favorites?.includes(selectedApp.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                  >
                    {preferences?.favorites?.includes(selectedApp.id) ? (
                      <Star size={22} className="text-[#f4af14] fill-[#f4af14]" />
                    ) : (
                      <Star size={22} className="text-gray-300 hover:text-[#f4af14]" />
                    )}
                  </button>
                  <span className="flex-1">{selectedApp.name}</span>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Description */}
                <div className="space-y-2">
                  <Label className="dark:text-gray-200 font-medium">Description</Label>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {selectedApp.description || "Aucune description disponible pour cette application."}
                    </p>
                  </div>
                </div>

                {/* Add to Group */}
                {preferences?.groups?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="dark:text-gray-200 font-medium">Ajouter à un groupe</Label>
                    <div className="flex flex-wrap gap-2">
                      {preferences.groups.map(group => {
                        const isInGroup = group.appIds?.includes(selectedApp.id);
                        return (
                          <button
                            key={group.id}
                            onClick={() => {
                              if (isInGroup) {
                                handleRemoveAppFromGroup(group.id, selectedApp.id);
                              } else {
                                handleAddAppToGroup(selectedApp.id, group.id);
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                              isInGroup 
                                ? 'bg-[#f76f4b] text-gray-800 dark:text-gray-900 hover:bg-[#e55d3b]' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            <Folder size={14} />
                            {group.name}
                            {isInGroup && <X size={12} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Open Button */}
                <Button
                  onClick={() => {
                    handleOpenApp(selectedApp);
                    if (!isAppOpenBlocked(selectedApp, user, scopeCtx)) {
                      setShowAppDetailsDialog(false);
                    }
                  }}
                  className={getAppOpenButtonClassName(isAppDisabled(selectedApp, scopeCtx), { fullWidth: true, compact: false }).replace('py-1.5', 'py-2')}
                >
                  {isAppOutOfSecteurScope(selectedApp, scopeCtx)
                    ? 'Changez de secteur'
                    : isAppOpenBlocked(selectedApp, user, scopeCtx)
                      ? 'Indisponible'
                      : <>Ouvrir l&apos;application <ExternalLink size={16} className="ml-2" /></>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <InactiveApplicationDialog
        open={inactiveAppDialog !== null}
        onOpenChange={(open) => { if (!open) setInactiveAppDialog(null); }}
        app={inactiveAppDialog}
      />

      {/* Modal de confirmation / changement de secteur (≥ 2 secteurs) */}
      <SecteurSelectorModal
        open={showSecteurModal}
        onClose={() => setShowSecteurModal(false)}
        onSecteurSelected={() => {
          loadData();
        }}
        token={token}
        updateUser={updateUser}
        user={user}
        userId={user?.id}
        onHidePreferenceSaved={(prefs) => {
          if (prefs) setPreferences(prefs);
        }}
      />

      {/* Modal d'affichage des applications d'un groupe */}
      <Dialog open={openGroupModal !== null} onOpenChange={(open) => !open && setOpenGroupModal(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-testid="group-apps-modal">
          {openGroupModal && (() => {
            const group = preferences.groups?.find(g => g.id === openGroupModal);
            const groupApps = group ? applications.filter(app => group.appIds?.includes(app.id)) : [];
            
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <div className="p-2 bg-[#f76f4b]/10 rounded-lg">
                      <Folder size={24} className="text-[#f76f4b]" />
                    </div>
                    <div>
                      <span className="text-xl">{group?.name}</span>
                      <p className="text-sm font-normal text-gray-500 dark:text-gray-400 mt-1">
                        {groupApps.length} application{groupApps.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4 overflow-y-auto max-h-[60vh] pr-2 -mr-2">
                  {groupApps.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-1">
                      {groupApps.map((app) => {
                        const disabled = isAppDisabled(app, scopeCtx);
                        const openBlocked = isAppOpenBlocked(app, user, scopeCtx);
                        const showPendingPulse = hasPendingDocuments && isPrysmeAccessApp(app);
                        return (
                        <div 
                          key={app.id}
                          className={`group rounded-xl p-4 transition-shadow duration-200 cursor-pointer border ${
                            disabled
                              ? 'bg-gray-200/70 dark:bg-gray-800/70 border-gray-200 dark:border-gray-700 opacity-60 saturate-50'
                              : 'bg-gray-50 dark:bg-gray-800 hover:shadow-lg border-gray-100 dark:border-gray-700 hover:border-[#f76f4b]/50'
                          }`}
                          onClick={() => {
                            handleOpenApp(app);
                            if (!disabled || user?.is_admin) setOpenGroupModal(null);
                          }}
                        >
                          {/* Bouton X pour retirer du groupe */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAppFromGroup(openGroupModal, app.id);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                            title="Retirer du groupe"
                          >
                            <X size={14} />
                          </button>
                          
                          {/* Header avec favoris */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-semibold truncate ${
                                disabled
                                  ? 'text-gray-400 dark:text-gray-500'
                                  : showPendingPulse
                                    ? 'pending-docs-app-list-name-blink font-semibold'
                                    : 'text-gray-900 dark:text-white'
                              }`}>
                                {app.name}
                              </h4>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFavorite(app.id);
                              }}
                              className="ml-2 flex-shrink-0"
                            >
                              <Star
                                size={18}
                                className={preferences.favorites?.includes(app.id)
                                  ? 'text-yellow-500 fill-yellow-500'
                                  : 'text-gray-300 hover:text-yellow-500'
                                }
                              />
                            </button>
                          </div>
                          
                          {/* Description */}
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                            {app.description || 'Aucune description'}
                          </p>
                          
                          {/* Bouton Ouvrir */}
                          <div className="flex items-center justify-end">
                            <span className={`text-sm font-medium flex items-center gap-1 ${disabled ? 'text-gray-400' : 'text-[#f76f4b] group-hover:underline'}`}>
                              {openBlocked ? 'Indisponible' : 'Ouvrir'}
                              {!openBlocked && <ExternalLink size={14} />}
                            </span>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Folder size={48} className="mx-auto mb-4 opacity-50" />
                      <p>Aucune application dans ce groupe</p>
                      <p className="text-sm mt-2">Glissez des applications sur ce groupe pour les ajouter</p>
                    </div>
                  )}
                </div>
                
                {/* Footer avec actions */}
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOpenGroupModal(null);
                      handleManageGroup(group);
                    }}
                    className="dark:border-gray-600"
                  >
                    <Settings size={16} className="mr-2" />
                    Gérer le groupe
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setOpenGroupModal(null);
                      handleDeleteGroup(openGroupModal);
                    }}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Supprimer
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
    </AppSecteurScopeContext.Provider>
  );
};

export default Home;
